import { CommonModule } from '@angular/common';
import { Component, Input, OnChanges, QueryList, ViewChild, ViewChildren } from '@angular/core';
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatMenu, MatMenuTrigger } from '@angular/material/menu';
import { TranslateModule } from '@ngx-translate/core';

import { IconModule } from 'src/app/icon/icon.module';
import { MaterialModule } from 'src/app/material.module';
import { FormControlType } from 'src/app/shared/enums';
import { IFilterTable } from 'src/app/shared/interfaces/ITable';
import { isEmptyValue } from 'src/app/shared/utils';

import { AutocompleteComponent } from '../../autocomplete/autocomplete.component';
import { FormSelectComponent } from '../../select/form-select.component';

@Component({
  selector: 'app-filter-table',
  standalone: true,
  imports: [
    IconModule,
    MaterialModule,
    TranslateModule,
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    FormSelectComponent,
    AutocompleteComponent
  ],
  templateUrl: './filter-table.component.html'
})
export class FilterTableComponent implements OnChanges {
  @Input() filterTableConfig?: IFilterTable;
  @Input() handleFilterTable!: (filterText: string) => void;
  @ViewChildren(AutocompleteComponent) autocompleteComponents!: QueryList<AutocompleteComponent>;
  @ViewChildren(FormSelectComponent) formSelectComponents!: QueryList<FormSelectComponent>;
  @ViewChild('filterMenu') filterMenu!: MatMenu;
  filterTableForm!: FormGroup;
  appliedValue: any = {};
  controlType = FormControlType;

  constructor(private formBuilder: FormBuilder) { }

  ngOnChanges(): void {
    if (!this.filterTableConfig) return;
    this.initForm();
  }

  initForm() {
    const controls: Record<string, any> = {};

    this.filterTableConfig!.items.forEach(item => {
      controls[item.key] = [item.defaultValue ?? null];
      this.appliedValue[item.key] = item.defaultValue ?? null;
    });

    this.filterTableForm = this.formBuilder.group(controls);
  }

  onMenuOpened() {
    this.filterTableForm.reset(this.appliedValue);
  }

  handleClearFilter() {
    // apply filter when clear all filter
    if(!Object.values(this.filterTableForm.value).every(value => !value)) {
      const empty: any = {};
      this.filterTableConfig!.items.forEach(item => empty[item.key] = null);
      this.filterTableForm.reset(empty);
      this.appliedValue = { ...this.filterTableForm.value };
      const filterText = this.buildFilterText(this.appliedValue);
      this.closeAllDropdowns();
      this.handleFilterTable(filterText);
    };
    this.filterMenu.closed.emit();
  }

  handleApplyFilter(trigger: MatMenuTrigger) {
    const formValue = this.filterTableForm.value;

    if (this.isSame(this.appliedValue, formValue)) return trigger.closeMenu();
    this.appliedValue = { ...formValue };
    const filterText = this.buildFilterText(this.appliedValue);
    this.handleFilterTable(filterText);
    trigger.closeMenu();
  }

  buildFilterText(value: any): string {
    if (!this.filterTableConfig) return '';

    return this.filterTableConfig.items.map(item => {
      const v = value[item.key];
      if (isEmptyValue(v)) return null;
      return `${item.key}[${item.paramOperator}]${v}`;
    }).filter(Boolean).join('&&');
  }

  isSame(a: any = {}, b: any = {}): boolean {
    return Object.keys({ ...a, ...b }).every(k => a[k] === b[k]);
  }

  get activeFilterCount(): number {
    return Object.values(this.appliedValue).filter(v => !isEmptyValue(v)).length;
  }

  onClickMenu(event: MouseEvent) {
    event.stopPropagation();
    
    const target = event.target as HTMLElement;
    const isClickOnFormSelect = target.closest('app-form-select');
    const isClickOnAutocomplete = target.closest('app-autocomplete');
    
    if (!isClickOnFormSelect && !isClickOnAutocomplete) {
      this.closeAllDropdowns();
    }
  }

  closeAllDropdowns() {
    // Close all autocomplete panels
    this.autocompleteComponents.forEach(autocomplete => {
      autocomplete.closeDropdown();
    });

    // Close all form-select dropdowns
    this.formSelectComponents.forEach(formSelect => {
      formSelect.closeDropdown();
    });
  }

  closeOtherDropdowns(excludeAutocomplete?: AutocompleteComponent, excludeFormSelect?: FormSelectComponent) {
    // Close all autocomplete panels except the excluded one
    this.autocompleteComponents.forEach(autocomplete => {
      if (autocomplete !== excludeAutocomplete) {
        autocomplete.closeDropdown();
      }
    });

    // Close all form-select dropdowns except the excluded one
    this.formSelectComponents.forEach(formSelect => {
      if (formSelect !== excludeFormSelect) {
        formSelect.closeDropdown();
      }
    });
  }

  getAutocompleteOpenHandler(autocomplete: AutocompleteComponent): () => void {
    return () => {
      this.closeOtherDropdowns(autocomplete, undefined);
    };
  }

  getFormSelectOpenHandler(formSelect: FormSelectComponent): () => void {
    return () => {
      this.closeOtherDropdowns(undefined, formSelect);
    };
  }
}
